import React, { Suspense } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { PublicRoute, PrivateRoute } from "@Components";
import {
    ForgotPasswordPage,
    HomePage,
    ShareChannelPage,
    LoginPage,
    SinglePoll,
    ProfileSettingsPage,
    ActivationPage,
    ReportPage,
} from "@Pages";
import RenewPassword from "@Pages/RenewPassword";
import Overview from "@Pages/Overview";
import { LoadingOverlay } from "@Styled/Pages/Login";

// Loaded on demand: the big screen has no use for the rest of the app's code.
const PresentScreenPage = React.lazy(() => import("@Pages/PresentScreen"));
const PresentRemotePage = React.lazy(() => import("@Pages/PresentRemote"));

interface ConfigRoutes {
    isPrivate?: boolean;
    path: string;
    component: React.ComponentType<any>;
    isRestricted?: boolean;
}

// route list
const routes: Array<ConfigRoutes> = [
    {
        isPrivate: true, // only logged user can access this route
        path: "/",
        component: Overview,
    },
    {
        isPrivate: true,
        path: "/profile-settings",
        component: ProfileSettingsPage,
    },
    {
        isPrivate: true,
        path: "/channel/:channelId",
        component: HomePage,
    },
    {
        path: "/activation",
        component: ActivationPage,
        isRestricted: true,
    },
    {
        path: "/login",
        component: LoginPage,
        isRestricted: true, //a logged in user CAN NOT access this route
    },
    {
        path: "/share",
        component: ShareChannelPage,
        isRestricted: false, //a logged in user CAN access this route
    },
    {
        path: "/single",
        component: SinglePoll,
        isRestricted: false,
    },
    {
        path: "/forgot-password",
        component: ForgotPasswordPage,
        isRestricted: true,
    },
    {
        path: "/recovery",
        component: RenewPassword,
        isRestricted: true,
    },
    {
        isPrivate: true,
        path: "/reports/:pollId",
        component: ReportPage,
    },
];

/**
 * Legacy redirect for links created by the retired Event Insights flow
 * (`/event-insights/:channelId`). Sends them to the channel (default tab); the
 * replacement content lives in its Report tab, which stays behind the usual
 * entitlement checks. Safe to remove once access logs show no hits.
 */
const LegacyEventInsightsRedirect: React.FC = () => {
    const { id } = useParams<"id">();

    return <Navigate to={id ? `/channel/${id}` : "/"} replace />;
};

const renderRoute = (
    { isPrivate, isRestricted, path, component: Component }: ConfigRoutes,
    index: number
) => {
    const element = isPrivate ? (
        <PrivateRoute component={Component} path={path} />
    ) : (
        <PublicRoute
            component={Component}
            path={path}
            isRestricted={isRestricted}
        />
    );

    return <Route key={path || index} path={path} element={element} />;
};

const router = () => (
    <Routes>
        {routes.map(renderRoute)}
        <Route
            path="/screen/:code"
            element={
                <Suspense fallback={<LoadingOverlay />}>
                    <PresentScreenPage />
                </Suspense>
            }
        />
        <Route
            path="/remote/:code"
            element={
                <Suspense fallback={<LoadingOverlay />}>
                    <PresentRemotePage />
                </Suspense>
            }
        />
        <Route
            path="/event-insights/:id?"
            element={<LegacyEventInsightsRedirect />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
);

export default router;
